import type { Ref } from "react";
import type { Item } from "../../../../api/menu";
import {
  getItemName,
  getItemUnitAndSize,
} from "../../../../utils/translations";
import { useTranslation } from "react-i18next";
import { isLunchItem, lunchRoomLeft } from "../../../../lib/lunchCatalog";
import { LunchCount, LunchTag } from "../../lunch/LunchTag";
import { highlightMatch, highlightPluCode } from "./highlight";
import {
  applyBillPercentToPrice,
  formatSignedBillPercent,
} from "../billDiscount";
import { tintBackground } from "./catalogColors";
import { CatalogQtyStepper } from "./CatalogQtyStepper";

interface ItemCardProps {
  item: Item;
  selected?: boolean;
  onSelect?: (itemId: number) => void;
  searchTerm?: string;
  quantity?: number;
  onQuantityChange?: (itemId: number, quantity: number) => void;
  // Знаков процент по сметката (клиент с отстъпка/надбавка) — цената се
  // показва като ~~стара~~ ±X% нова; 0 = без корекция
  billDiscountPercent?: number;
  // Реф към DOM картата — ItemCatalog я ползва за keyboard позиции и scroll-into-view
  ref?: Ref<HTMLDivElement>;
  // Цвят на картата по настройката „оцветяване" (станция/група); null = без фон
  tint?: string | null;
  // Моливът в степера — отваря EditModifiersModal за продукта
  onEdit?: (itemId: number) => void;
}

/**
 * Продуктова карта в грид изгледа на каталога (настройка „Грид" от
 * Конфигурация → Маркиране, 0.0.131) — възстановеният изглед отпреди
 * редизайна 0.0.116, преведен на pos-* токените: име на 2 реда, цена и —
 * за да пести място — ИЛИ грамажът (без количество), ИЛИ степерът − N +
 * | молив (при маркирано количество). Кликът (както при реда) добавя +1 — гейтът
 * за задължителни добавки/модификатори отваря „Промени" при нужда.
 */
export const ItemCard = ({
  item,
  selected = false,
  onSelect,
  searchTerm = "",
  quantity = 0,
  onQuantityChange,
  billDiscountPercent = 0,
  ref,
  tint = null,
  onEdit,
}: ItemCardProps) => {
  const { i18n } = useTranslation();
  const itemName = getItemName(item.itemLangs, i18n.language, item.slug);
  const unitAndSize = getItemUnitAndSize(item.itemLangs, i18n.language);
  const hasQty = quantity > 0;
  // Обеден продукт с дневен лимит: колко още могат да се добавят (без
  // кошницата); 0 → сив и „Изчерпано". Ограничението е в Ordering.
  const lunchLeft = lunchRoomLeft(item, quantity);
  const lunchRemaining = Number.isFinite(lunchLeft) ? lunchLeft : null;
  const lunchSoldOut = lunchRemaining === 0 && !hasQty;
  // Заключената цена не се коригира от клиентския процент (както в BE)
  const showAdjustedPrice = billDiscountPercent !== 0 && !item.lockedPrice;
  const adjustedPrice = applyBillPercentToPrice(
    item.price,
    billDiscountPercent,
  );

  return (
    <div
      ref={ref}
      onClick={() => onSelect?.(item.id)}
      // Фонът в цвета на станцията/групата идва от данните → inline style.
      // Изчерпан обеден продукт остава сив (цветът би бил bg-pos-line2).
      style={{
        background:
          tint && !lunchSoldOut
            ? tintBackground(tint, hasQty ? 32 : 18)
            : undefined,
      }}
      // Селектираната (keyboard фокусираната) карта леко „изплува" — scale +
      // сянка, както преди редизайна 0.0.116
      aria-disabled={lunchSoldOut || undefined}
      className={`relative px-2 py-1.5 rounded-xl border cursor-pointer flex flex-col gap-0.5 transition-all duration-200 ${
        lunchSoldOut ? "bg-pos-line2" : hasQty ? "bg-pos-marked" : "bg-pos-card"
      } ${
        selected
          ? // Курсорът = inset пръстен в мастило (както в списъка) + леко
            // „изплуване"; рамката остава неутрална
            // Без оцветяване — кехлибарен пръстен (както преди); при оцветена
            // карта — мастило, което стои върху всеки цвят
            `ring-1 ring-inset ${tint ? "ring-pos-ink" : "ring-pos-accent"} border-pos-line3 z-10 scale-[1.05] shadow-[0_18px_38px_rgba(17,17,17,0.18),0_4px_12px_rgba(17,17,17,0.08)]`
          : hasQty
            ? "border-pos-accent/30"
            : "border-pos-line3 hover:border-pos-accent/35"
      }`}
    >
      {/* Име — до 2 реда, с highlight на търсенето */}
      {/* „Обедно" / „Изчерпано" — вдясно на реда с името */}
      <div className="min-h-[35px] flex items-start gap-2">
        <span
          className={`flex-1 min-w-0 text-[15px] font-bold leading-[1.3] line-clamp-2 break-words ${
            lunchSoldOut ? "text-pos-muted" : "text-pos-ink"
          }`}
        >
          {highlightMatch(itemName, searchTerm)}
          {item.pluCode != null && (
            <span className="font-pos-mono text-[13px] font-medium text-pos-muted">
              {" "}
              | {highlightPluCode(item.pluCode, searchTerm)}
            </span>
          )}
        </span>
        {isLunchItem(item) && <LunchTag soldOut={lunchSoldOut} className="mt-px" />}
      </div>

      {/* Мета (цена + грамаж) вляво · − / + вдясно */}
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 flex flex-col gap-px leading-[1.1]">
          {showAdjustedPrice ? (
            <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
              <span className="font-pos-mono text-[13px] text-pos-muted line-through opacity-70">
                {item.price.toFixed(2)}
              </span>
              <span className="text-[12px] font-semibold text-pos-disc-ink">
                {formatSignedBillPercent(billDiscountPercent)}
              </span>
              <span className="font-pos-mono text-[14px] font-semibold text-pos-ink2">
                {adjustedPrice.toFixed(2)} €
              </span>
            </span>
          ) : (
            <span
              className={`font-pos-mono text-[14px] font-semibold whitespace-nowrap ${
                lunchSoldOut ? "text-pos-muted" : "text-pos-ink2"
              }`}
            >
              {item.price.toFixed(2)} €
            </span>
          )}
        </span>
        {/* Остават „N бр" | грамаж — остават в потока и когато има степер
            (само невидими): редът на цената пази същата височина и картата
            не се свива. */}
        {(unitAndSize || lunchRemaining != null) && (
          <span
            className={`min-w-0 flex items-center gap-1.5 font-pos-mono text-[12.5px] ${hasQty ? "invisible" : ""}`}
          >
            {lunchRemaining != null && <LunchCount count={lunchRemaining} />}
            {lunchRemaining != null && unitAndSize && (
              <span aria-hidden className="h-3 w-px shrink-0 bg-pos-ctl" />
            )}
            {unitAndSize && <span className="truncate text-pos-muted">{unitAndSize}</span>}
          </span>
        )}
        {hasQty && (
          /* Степер − N + | молив (като в списъка) — ИЗВЪН потока: абсолютно
             долу вдясно, долният му ръб е на линията на цената, за да не
             прави картата по-висока (и целия грид ред с нея); нагоре стъпва
             върху долната част на зоната на името — нарочно, името не се
             свива. */
          /* Обеден продукт: етикетът „Обедно" е горе вдясно — степерът се
             закача на ФИКСИРАНО разстояние под него (31px = мястото му в
             карта с едноредово име), не за дъното: по-висок грид ред (друга
             карта с двуредово име) иначе раздалечава етикета и бутоните. */
          <span
            className={`absolute right-2 ${isLunchItem(item) ? "top-[31px]" : "bottom-1.5"}`}
          >
            <CatalogQtyStepper
              quantity={quantity}
              onDecrement={() =>
                onQuantityChange?.(item.id, Math.max(0, quantity - 1))
              }
              onIncrement={() => onQuantityChange?.(item.id, quantity + 1)}
              onEdit={() => onEdit?.(item.id)}
              incrementDimmed={lunchLeft === 0}
            />
          </span>
        )}
      </div>
    </div>
  );
};
